import { useEffect, useRef, useState } from 'react';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

/**
 * On a phone, clamps a long list to `collapsedHeight` with a "Show more"
 * button under it, so the page is the only thing that scrolls and opening
 * the list is a deliberate tap (ERIN-8561). Elsewhere it renders its children
 * untouched, so desktop cards keep their own scroll boxes.
 */
export const PhoneExpandable = ({
	children,
	collapsedHeight = 320,
	moreLabel = 'Show more',
	lessLabel = 'Show less',
	className = '',
}) => {
	const [open, setOpen] = useState(false);
	const [canExpand, setCanExpand] = useState(false);
	const bodyRef = useRef(null);
	const rootRef = useRef(null);

	useEffect(() => {
		const body = bodyRef.current;
		if (!body) return undefined;
		const check = () => {
			const phone = document.documentElement.classList.contains('erin-phone');
			setCanExpand(phone && body.scrollHeight > collapsedHeight + 24);
		};
		check();
		const ro =
			typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(check);
		ro?.observe(body);
		window.addEventListener('resize', check);
		return () => {
			ro?.disconnect();
			window.removeEventListener('resize', check);
		};
	}, [collapsedHeight, children]);

	const toggle = () => {
		const next = !open;
		setOpen(next);
		if (!next) {
			rootRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
		}
	};

	const clamped = canExpand && !open;

	return (
		<div
			ref={rootRef}
			className={`phone-expandable${clamped ? ' is-clamped' : ''} ${className}`.trim()}
			style={
				clamped ? { '--collapsed-height': `${collapsedHeight}px` } : undefined
			}
		>
			<div ref={bodyRef} className="phone-expandable-body">
				{children}
			</div>
			{canExpand && (
				<button
					type="button"
					className="phone-expandable-toggle"
					aria-expanded={open}
					onClick={toggle}
				>
					<span>{open ? lessLabel : moreLabel}</span>
					{open ? <UpOutlined /> : <DownOutlined />}
				</button>
			)}
		</div>
	);
};

PhoneExpandable.propTypes = {
	children: PropTypes.node,
	collapsedHeight: PropTypes.number,
	moreLabel: PropTypes.node,
	lessLabel: PropTypes.node,
	className: PropTypes.string,
};

export default PhoneExpandable;
